/**
 * Composing a message out of sections — 16 Sep 2026.
 *
 * A list of bands, each with its own background, padding and alignment, and
 * each optionally addressed at one kind of reader. The writer edits the
 * sections; `renderEmail` turns them into the tables an inbox needs, and the
 * preview beside them shows the result rather than a description of it.
 *
 * ## Reordering is buttons, not dragging
 *
 * Move up and move down. Drag and drop looks better in a screenshot and is
 * worse in use: it needs a pointer, it has no keyboard equivalent without
 * writing one, and on a list where the items are different heights it is a game
 * of aim. Two buttons are unambiguous, work from the keyboard for free, and
 * cannot drop a section somewhere nobody meant.
 *
 * ## The preview has a tier switch, and it matters more than it looks
 *
 * A message with conditional sections is several messages. Showing only the
 * composed version — every section at once — would mean nobody ever sees what a
 * paying subscriber actually receives, and the first person to see it is the
 * subscriber. The switch renders exactly the cut the server will make, through
 * the same function.
 */
import { useState } from 'react'
import {
  newBlock,
  PADDING,
  paddingOf,
  READER_TIERS,
  type BlockKind,
  type BlockPadding,
  type EmailBlock,
  type EmailDocument,
  type ReaderSegment,
} from '@/types/emailBlocks'
import { renderForTier } from './renderEmail'
import { Flag, LanguageFlag } from '@/shared/places/Flag'
import { COMMON_COUNTRIES, LANGUAGES, LANGUAGE_NAMES, countryName, isLanguage } from '@/shared/places/places'
import { FILLED_LABEL, MERGE_TAGS, previewMerge, unknownTags } from './mergeFields'
import { useMergeInsert } from './useMergeInsert'
import { callFunction } from '@/shared/firebase/callFunction'

/** One reader's real facts, as `crmReaderFacts` answers them. */
interface Reader {
  segments: ReaderSegment[]
  merge: Record<string, string>
  /** When the route and collection counts were last built. */
  builtAtMs: number
}
import { BlockFields } from './BlockFields'
import './BlockEditor.css'

/**
 * What can be added, in the order the handover lists them.
 *
 * The components come first because they are what a message is mostly made of;
 * the primitives sit at the end for the cases the design system did not
 * anticipate.
 */
const KINDS: { key: BlockKind; label: string }[] = [
  { key: 'header', label: 'Header' },
  { key: 'heading', label: 'Headline' },
  { key: 'text', label: 'Text' },
  { key: 'button', label: 'Button' },
  { key: 'notice', label: 'Dated notice' },
  { key: 'stats', label: 'Stat row' },
  { key: 'card', label: 'Card' },
  { key: 'steps', label: 'Steps' },
  { key: 'image', label: 'Image' },
  { key: 'footer', label: 'Footer' },
  { key: 'divider', label: 'Divider' },
  { key: 'spacer', label: 'Spacer' },
]

const PADDINGS: BlockPadding[] = ['none', 'small', 'medium', 'large']

export function BlockEditor({
  value,
  onChange,
  subject = '',
}: {
  value: EmailDocument
  onChange: (next: EmailDocument) => void
  /**
   * The subject, for the preview and the stray check only — it is edited in the
   * workspace header, not here. It takes merge tags like the body does, so a
   * typo in it has to be caught by the same warning, and the preview has to
   * show what the inbox will show.
   */
  subject?: string
}) {
  const [open, setOpen] = useState<string>('')
  /** Which reader the preview is showing. Empty means "as composed". */
  const [asTier, setAsTier] = useState<ReaderSegment | ''>('')
  /** The preview's language and country — 28 Oct 2026. Empty shows every one. */
  const [asLang, setAsLang] = useState('')
  const [asCountry, setAsCountry] = useState('')
  /** A country code being typed into a section's own list. */
  const [countryDraft, setCountryDraft] = useState('')
  /**
   * Where a merge tag goes, and what to say when there is nowhere.
   *
   * The panel inserts at the caret of whichever field was last focused — the
   * subject line in the header counts, which is the point. Pressing a tag with
   * nothing focused has nowhere to put it, and saying so beats appearing to do
   * nothing.
   */
  const { insert } = useMergeInsert()
  const [insertNote, setInsertNote] = useState<string | null>(null)
  /**
   * A named reader to preview as — 21 Sep 2026.
   *
   * The sample values show that a sentence reads well. They cannot show that
   * `{{routes}}` is empty for the eighteen thousand people who have never drawn
   * one, or that a section addressed at `has-tracker` reaches almost nobody. So
   * an address can be named, and the preview then uses that account's real
   * segments and real figures — the same two things the send would use.
   */
  const [readerEmail, setReaderEmail] = useState('')
  const [reader, setReader] = useState<Reader | null>(null)
  const [readerBusy, setReaderBusy] = useState(false)
  const [readerNote, setReaderNote] = useState<string | null>(null)

  const lookUpReader = async () => {
    const email = readerEmail.trim().toLowerCase()
    if (!email.includes('@')) return
    setReaderBusy(true)
    setReaderNote(null)
    try {
      const out = await callFunction<Reader & { found: boolean }>('crmReaderFacts', {
        method: 'POST',
        body: { email },
      })
      if (!out.found) {
        setReader(null)
        setReaderNote(`${email} is not an app account, so there are no figures to show.`)
        return
      }
      setReader({ segments: out.segments, merge: out.merge, builtAtMs: out.builtAtMs })
      // The chips and a named reader are two answers to the same question, so
      // choosing one clears the other rather than leaving the screen ambiguous
      // about which cut it is showing.
      setAsTier('')
    } catch (e) {
      setReader(null)
      setReaderNote(e instanceof Error ? e.message : 'That address could not be looked up.')
    } finally {
      setReaderBusy(false)
    }
  }

  const clearReader = () => {
    setReader(null)
    setReaderNote(null)
  }

  const blocks = value.blocks
  /**
   * Tags nothing will ever fill — a typo, or `{{navn}}` carried over from the
   * shareholder form, which is a different merge with a different vocabulary.
   * Checked against the whole document because a tag can be in any field.
   */
  const strays = unknownTags(
    [subject, ...blocks.map((b) => `${b.html} ${b.label} ${b.href} ${b.items.map((i) => `${i.a} ${i.b}`).join(' ')}`)].join(' '),
  )
  /**
   * Which readers the preview offers to render as.
   *
   * The five tiers always, because "what does a paying subscriber see" is a
   * question worth asking of any message. The PRO and activity facts only where
   * a section actually names one — there are ten of them, and a row of fifteen
   * chips above a message that conditions on nothing is a switch for choices
   * that do not exist.
   */
  const addressed = new Set(value.blocks.flatMap((b) => b.visibleTo))
  const previewAs = READER_TIERS.filter((t) => t.group === 'tier' || addressed.has(t.key))

  const set = (next: Partial<EmailDocument>) => onChange({ ...value, ...next })
  const patch = (id: string, p: Partial<EmailBlock>) =>
    set({ blocks: blocks.map((b) => (b.id === id ? { ...b, ...p } : b)) })

  const add = (kind: BlockKind) => {
    const b = newBlock(kind)
    set({ blocks: [...blocks, b] })
    setOpen(b.id)
  }

  const move = (i: number, by: -1 | 1) => {
    const j = i + by
    if (j < 0 || j >= blocks.length) return
    const next = [...blocks]
    const [row] = next.splice(i, 1)
    next.splice(j, 0, row!)
    set({ blocks: next })
  }

  const toggleIn = (list: string[] | undefined, x: string) =>
    (list ?? []).includes(x) ? (list ?? []).filter((y) => y !== x) : [...(list ?? []), x]
  /** The languages and countries any section names, for the preview's pickers. */
  const usedLangs = [...new Set(blocks.flatMap((b) => b.languages ?? []))]
  const usedCountries = [...new Set(blocks.flatMap((b) => b.countries ?? []))]

  const toggleTier = (b: EmailBlock, tier: ReaderSegment) =>
    patch(b.id, {
      visibleTo: b.visibleTo.includes(tier)
        ? b.visibleTo.filter((t) => t !== tier)
        : [...b.visibleTo, tier],
    })

  return (
    <div className="blk">
      {/*
        The palette, as its own column — 17 Sep 2026.

        It was a row of "+ Text" buttons under the last section, which put the
        thing you reach for most at the bottom of a list that grows. A rail
        stays where it is however long the message gets, and reads as a
        catalogue of what a message is made of rather than as an afterthought.
      */}
      <aside className="blk__palette">
        <span className="card-label">Add</span>
        {KINDS.map((k) => (
          <button key={k.key} className="blk__add" onClick={() => add(k.key)}>
            {k.label}
          </button>
        ))}

        {/*
          The reader's own facts, put in at the caret — 18 Sep 2026.

          Under the components rather than beside them, because a merge tag is
          something you reach for mid-sentence and the sentence is already in
          the editor. Each button says how much of the list actually has that
          field: `{{firstname}}` is filled on about half of app accounts, and a
          writer who knows that writes `{{firstname|der}}` instead of opening
          twenty thousand messages with a comma.
        */}
        <span className="card-label blk__palettehead">Reader</span>
        {MERGE_TAGS.map((t) => (
          <button
            key={t.tag}
            className="blk__add blk__add--tag"
            title={`${t.note ? t.note + ' ' : ''}Filled for ${FILLED_LABEL[t.filled]}. Example: ${t.sample}`}
            onClick={() => {
              if (!insert(t.tag)) setInsertNote('Put the cursor in a field first.')
              else setInsertNote(null)
            }}
          >
            <span>{t.label}</span>
            <span className="blk__tagwho">{FILLED_LABEL[t.filled]}</span>
          </button>
        ))}
        <p className="micro">
          Add a fallback after a bar — <code>{'{{firstname|der}}'}</code> — for the readers we have
          no value for.
        </p>
        {insertNote && <p className="blk__tagnote">{insertNote}</p>}
      </aside>

      <div className="blk__editor">
        <div className="blk__page">
          <label className="blk__pagefield">
            <span className="card-label">Page background</span>
            <input
              type="color"
              value={value.background}
              onChange={(e) => set({ background: e.target.value })}
            />
          </label>
          <label className="blk__pagefield">
            <span className="card-label">Width</span>
            <input
              className="input blk__width"
              type="number"
              min={320}
              max={800}
              step={20}
              value={value.width}
              onChange={(e) => set({ width: Number(e.target.value) || 600 })}
            />
          </label>
        </div>

        {blocks.length === 0 && (
          <p className="empty-note">No sections yet. Add one below to start the message.</p>
        )}

        {blocks.map((b, i) => (
          <div className="blk__row" key={b.id}>
            <div className="blk__bar">
              <button
                className="blk__grip"
                aria-expanded={open === b.id}
                onClick={() => setOpen(open === b.id ? '' : b.id)}
              >
                <span className="blk__kind">{KINDS.find((k) => k.key === b.kind)?.label}</span>
                <span className="blk__summary">{summarise(b)}</span>
                {b.visibleTo.length > 0 && (
                  <span className="blk__only">
                    Only {b.visibleTo.map((t) => tierLabel(t)).join(', ')}
                  </span>
                )}
                {((b.languages?.length ?? 0) > 0 || (b.countries?.length ?? 0) > 0) && (
                  <span className="blk__only blk__places">
                    {(b.languages ?? []).map((l) => (
                      <LanguageFlag key={l} language={l} />
                    ))}
                    {(b.countries ?? []).map((c) => (
                      <Flag key={c} code={c} />
                    ))}
                  </span>
                )}
              </button>
              <div className="blk__moves">
                <button className="blk__icon" disabled={i === 0} onClick={() => move(i, -1)} title="Move up">
                  ↑
                </button>
                <button
                  className="blk__icon"
                  disabled={i === blocks.length - 1}
                  onClick={() => move(i, 1)}
                  title="Move down"
                >
                  ↓
                </button>
                <button
                  className="blk__icon blk__icon--bad"
                  onClick={() => set({ blocks: blocks.filter((x) => x.id !== b.id) })}
                  title="Remove section"
                >
                  ✕
                </button>
              </div>
            </div>

            {open === b.id && (
              <div className="blk__form">
                <BlockFields b={b} patch={(pp) => patch(b.id, pp)} />

                <div className="blk__style">
                  <label className="blk__pagefield">
                    <span className="card-label">Background</span>
                    <input
                      type="color"
                      value={b.background}
                      onChange={(e) => patch(b.id, { background: e.target.value })}
                    />
                  </label>
                  <label className="blk__pagefield">
                    <span className="card-label">{b.kind === 'button' ? 'Button' : 'Text'}</span>
                    <input
                      type="color"
                      value={b.color}
                      onChange={(e) => patch(b.id, { color: e.target.value })}
                    />
                  </label>
                  <label className="blk__pagefield">
                    <span className="card-label">Padding top</span>
                    <select
                      className="input blk__select"
                      value={b.padding}
                      onChange={(e) =>
                        // Bottom stays where it was: it followed the top only
                        // while nobody had set it.
                        patch(b.id, {
                          padding: e.target.value as BlockPadding,
                          paddingBottom: b.paddingBottom ?? b.padding,
                        })
                      }
                    >
                      {PADDINGS.map((p) => (
                        <option key={p} value={p}>
                          {p} · {PADDING[p]}px
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="blk__pagefield">
                    <span className="card-label">Padding bottom</span>
                    <select
                      className="input blk__select"
                      value={b.paddingBottom ?? b.padding}
                      onChange={(e) => patch(b.id, { paddingBottom: e.target.value as BlockPadding })}
                    >
                      {PADDINGS.map((p) => (
                        <option key={p} value={p}>
                          {p} · {PADDING[p]}px
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="blk__pagefield">
                    <span className="card-label">Align</span>
                    <select
                      className="input blk__select"
                      value={b.align}
                      onChange={(e) =>
                        patch(b.id, { align: e.target.value as EmailBlock['align'] })
                      }
                    >
                      <option value="left">left</option>
                      <option value="center">center</option>
                      <option value="right">right</option>
                    </select>
                  </label>
                </div>

                <div className="blk__who">
                  <span className="card-label">Show this section to</span>
                  <div className="blk__chips">
                    {READER_TIERS.map((t, n) => (
                      <span className="blk__chipwrap" key={t.key}>
                        {/* A rule where the question changes: the five tiers are
                            one answer each, the two PRO facts are separate
                            things that can be true alongside any of them. */}
                        {n > 0 && READER_TIERS[n - 1]!.group !== t.group && (
                          <span className="blk__chiprule" aria-hidden="true" />
                        )}
                        <button
                          className="chip"
                          aria-pressed={b.visibleTo.includes(t.key)}
                          title={t.note}
                          onClick={() => toggleTier(b, t.key)}
                        >
                          {t.label}
                        </button>
                      </span>
                    ))}
                  </div>
                  <p className="micro">
                    {b.visibleTo.length === 0
                      ? 'Everybody. Pick one or more to address this section at them alone.'
                      : 'Shown to a reader who matches any of these. PRO membership sits alongside what somebody pays, so “Paying” and “PRO admin” together reaches either. Check it with the preview switch.'}
                  </p>
                </div>

                {/* Where the reader is — 28 Oct 2026. Its own question, so it
                    narrows the choice above rather than adding to it. */}
                <div className="blk__who">
                  <span className="card-label">Language</span>
                  <div className="blk__chips">
                    {LANGUAGES.map((l) => (
                      <button
                        key={l}
                        className="chip blk__placechip"
                        aria-pressed={(b.languages ?? []).includes(l)}
                        onClick={() => patch(b.id, { languages: toggleIn(b.languages, l) })}
                      >
                        <LanguageFlag language={l} /> {LANGUAGE_NAMES[l]}
                      </button>
                    ))}
                  </div>
                  <span className="card-label">Country</span>
                  <div className="blk__chips">
                    {[...new Set([...COMMON_COUNTRIES, ...(b.countries ?? [])])].map((c) => (
                      <button
                        key={c}
                        className="chip blk__placechip"
                        aria-pressed={(b.countries ?? []).includes(c)}
                        title={countryName(c)}
                        onClick={() => patch(b.id, { countries: toggleIn(b.countries, c) })}
                      >
                        <Flag code={c} /> {c}
                      </button>
                    ))}
                    <form
                      className="blk__countryadd"
                      onSubmit={(e) => {
                        e.preventDefault()
                        const code = countryDraft.trim().toUpperCase()
                        if (/^[A-Z]{2}$/.test(code) && !(b.countries ?? []).includes(code)) {
                          patch(b.id, { countries: [...(b.countries ?? []), code] })
                        }
                        setCountryDraft('')
                      }}
                    >
                      <input
                        className="input"
                        value={countryDraft}
                        maxLength={2}
                        placeholder="Other, e.g. JP"
                        onChange={(e) => setCountryDraft(e.target.value)}
                      />
                    </form>
                  </div>
                  <p className="micro">
                    {(b.languages?.length ?? 0) === 0 && (b.countries?.length ?? 0) === 0
                      ? 'Every language and country.'
                      : 'Only readers in one of these languages and, if any are picked, one of these countries — on top of the choice above. A reader whose country is unknown (not an app user) counts as English, in no country.'}
                  </p>
                </div>
              </div>
            )}
          </div>
        ))}

      </div>

      <div className="blk__previewpane">
        <div className="blk__previewhead">
          <span className="card-label">Preview</span>
          {strays.length > 0 && (
            <span className="blk__stray" title="Nothing fills these, so they will be sent as written">
              Unknown: {strays.map((t) => `{{${t}}}`).join(' ')}
            </span>
          )}
          <div className="blk__chips">
            <button
              className="chip"
              aria-pressed={asTier === '' && !reader}
              onClick={() => {
                setAsTier('')
                clearReader()
              }}
            >
              As composed
            </button>
            {usedLangs.length > 0 && (
              <select
                className="input blk__previewselect"
                value={asLang}
                aria-label="Preview language"
                onChange={(e) => {
                  setAsLang(e.target.value)
                  clearReader()
                }}
              >
                <option value="">Every language</option>
                {LANGUAGES.map((l) => (
                  <option key={l} value={l}>
                    {LANGUAGE_NAMES[l]}
                  </option>
                ))}
              </select>
            )}
            {usedCountries.length > 0 && (
              <select
                className="input blk__previewselect"
                value={asCountry}
                aria-label="Preview country"
                onChange={(e) => {
                  setAsCountry(e.target.value)
                  clearReader()
                }}
              >
                <option value="">Every country</option>
                {usedCountries.map((c) => (
                  <option key={c} value={c}>
                    {countryName(c)}
                  </option>
                ))}
                <option value="--">Somewhere else</option>
              </select>
            )}
            {previewAs.map((t, n) => (
              <span className="blk__chipwrap" key={t.key}>
                {n > 0 && previewAs[n - 1]!.group !== t.group && (
                  <span className="blk__chiprule" aria-hidden="true" />
                )}
                <button
                  className="chip"
                  aria-pressed={asTier === t.key}
                  title={t.note}
                  onClick={() => {
                    setAsTier(t.key)
                    clearReader()
                  }}
                >
                  {t.label}
                </button>
              </span>
            ))}
          </div>
        </div>
        {/*
          Preview as somebody in particular.

          Below the chips rather than beside them: the chips answer "what does a
          paying subscriber see", this answers "what does Ingrid see", and the
          second question is the one that catches an empty merge field.
        */}
        <form
          className="blk__readerrow"
          onSubmit={(e) => {
            e.preventDefault()
            void lookUpReader()
          }}
        >
          <input
            className="input"
            type="email"
            value={readerEmail}
            placeholder="Preview as… an app user's email"
            onChange={(e) => setReaderEmail(e.target.value)}
          />
          <button className="btn btn--ghost btn--tiny" disabled={readerBusy || !readerEmail.trim()}>
            {readerBusy ? 'Looking…' : 'Preview as'}
          </button>
          {reader && (
            <button type="button" className="btn btn--ghost btn--tiny" onClick={clearReader}>
              Clear
            </button>
          )}
        </form>
        {reader && (
          <p className="micro blk__readerwho">
            {reader.segments.map((t) => tierLabel(t)).join(' · ')}
            {reader.builtAtMs > 0 &&
              ` · counted ${new Date(reader.builtAtMs).toLocaleString('nb-NO')}`}
          </p>
        )}
        {readerNote && <p className="blk__tagnote">{readerNote}</p>}

        {/* The subject as the inbox will list it, which is the only place a
            writer can see whether their merge tag reads as a sentence there. */}
        {subject.includes('{{') && (
          <p className="blk__previewsubject">
            <span className="card-label">Subject</span> {previewMerge(subject, reader?.merge)}
          </p>
        )}
        {/* The renderer's own output, which is the string that will be sent —
            the preview cannot drift from the message because it is the message. */}
        <div
          className="blk__preview"
          dangerouslySetInnerHTML={{
            __html: renderForTier(
              value,
              reader ? reader.segments : previewSegments(asTier, asLang, asCountry),
              reader?.merge,
            ),
          }}
        />
      </div>
    </div>
  )
}

const tierLabel = (t: ReaderSegment): string => {
  if (t.startsWith('lang:')) {
    const l = t.slice(5)
    return isLanguage(l) ? LANGUAGE_NAMES[l] : l
  }
  if (t.startsWith('country:')) return countryName(t.slice(8))
  return READER_TIERS.find((x) => x.key === t)?.label ?? t
}

/**
 * What the preview renders as: the tier chip, plus the language and country
 * picked. "Somewhere else" is a country no section names, so every
 * country-limited section drops out.
 */
function previewSegments(tier: ReaderSegment | '', lang: string, country: string): ReaderSegment[] | null {
  const out: ReaderSegment[] = []
  if (tier) out.push(tier)
  if (lang) out.push(`lang:${lang}`)
  if (country) out.push(`country:${country === '--' ? 'ZZ' : country}`)
  return out.length ? out : null
}

/** A line of the section's content, for the collapsed row. */
function summarise(b: EmailBlock): string {
  const rows = b.items.filter((i) => i.a.trim() !== '')
  switch (b.kind) {
    case 'text': {
      const text = b.html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
      return text.length > 60 ? `${text.slice(0, 60)}…` : text || 'Empty'
    }
    case 'heading':
      return b.label || 'No headline yet'
    case 'header':
      return b.variant === 'dark' ? 'Wordmark on indigo' : 'Wordmark on white'
    case 'image': {
      if (!b.src) return 'No image yet'
      const width =
        b.variant === 'full' ? 'full width' : b.variant === 'natural' ? 'own size' : 'column'
      return [width, b.ratio || 'original shape', b.href ? 'links out' : ''].filter(Boolean).join(' · ')
    }
    case 'button':
      return b.href ? `${b.label} → ${b.href}` : `${b.label} — no link`
    case 'notice':
      return `${b.label} · ${b.html}`
    case 'stats':
      return rows.length ? rows.map((i) => `${i.a} ${i.b}`.trim()).join(' · ') : 'No figures yet'
    case 'card':
      return rows[0]?.a || 'No title yet'
    case 'steps':
      return rows.length ? `${rows.length} steps · ${rows.map((i) => i.a).join(', ')}` : 'No steps yet'
    case 'footer':
      return b.variant === 'transactional' ? 'Transactional footer' : 'Marketing footer'
    case 'divider':
      return 'A rule across the column'
    case 'spacer':
      return `${paddingOf(b).top + paddingOf(b).bottom}px of space`
  }
}
